import {CommentsCategory, type CommentDto} from '@domain/comments'
import type {BaseSchedule} from '@domain/schedule'
import {dataTestId} from '@hconnect/uikit'
import {ReactComponent as NoCommentsImage} from '@hconnect/uikit/src/lib2/components/comment/noComments.svg'
import {useUpmPlantContext} from '@hconnect/upm'
import {DeleteOutline} from '@mui/icons-material'
import {Stack, Typography} from '@mui/material'
import {Moment} from 'moment-timezone'
import React, {useCallback} from 'react'
import {useTranslation} from 'react-i18next'

import {PLANNING_COMMENTS_WIDGET_NAMESPACE} from '../consts'
import {useConfirmDialog, useDeleteCommentMutation} from '../hooks'
import {useSelectedAssetId} from '../providers'

import {PlanningCommentItem} from './PlanningCommentItem'

interface CommentSidebarListProps {
  comments: CommentDto[]
  isReadOnly: boolean
  schedule: BaseSchedule
  startOfPlan: Moment
  endOfPlan: Moment
  onSelectScheduleItem?: (id: string) => void
}

export const CommentSidebarList: React.FC<CommentSidebarListProps> = ({
  comments,
  isReadOnly,
  schedule,
  startOfPlan,
  endOfPlan,
  onSelectScheduleItem
}) => {
  const {t} = useTranslation(PLANNING_COMMENTS_WIDGET_NAMESPACE)
  const {plantId: plantCode} = useUpmPlantContext()
  const {openDialog, ConfirmDialog} = useConfirmDialog()
  const {setAssetId} = useSelectedAssetId()

  const {mutate: deleteComment} = useDeleteCommentMutation()

  const handleEdit = useCallback(
    ({commentsCategoryInstanceId, commentsCategory}: CommentDto) => {
      const instanceId = commentsCategoryInstanceId
      switch (commentsCategory) {
        case CommentsCategory.Assets:
          return setAssetId(Number(instanceId))
        case CommentsCategory.AssetOperationTimes: {
          const {id} = schedule.schedules[instanceId]
          return onSelectScheduleItem?.(id)
        }
        default:
          throw new Error('Unsupported comment category')
      }
    },
    [schedule, setAssetId, onSelectScheduleItem]
  )

  const handleDelete = useCallback(
    ({commentsCategory, commentsCategoryInstanceId, commentId}: CommentDto) => {
      openDialog({
        title: t('planning.deleteComment'),
        description: t('planning.deleteCommentConfirmation'),
        mainAction: {
          color: 'error',
          text: t('common.delete'),
          icon: <DeleteOutline />,
          onAction: () =>
            deleteComment({
              plantId: plantCode,
              commentsCategory,
              commentCategoryInstanceId: commentsCategoryInstanceId,
              commentId
            })
        }
      })
    },
    [openDialog, t, deleteComment, plantCode]
  )

  const isEmpty = comments.length === 0

  return (
    <>
      {ConfirmDialog}
      <Stack
        sx={{
          flex: 1,
          ...(isEmpty && {
            justifyContent: 'center',
            alignItems: 'center'
          })
        }}
        spacing={3}
      >
        {isEmpty ? (
          <>
            <NoCommentsImage {...dataTestId('no_comments_image')} />
            <Typography
              variant="body1"
              sx={{textAlign: 'center'}}
              {...dataTestId('no_comments_info')}
            >
              {t('planning.noCommentsInfo')}
            </Typography>
          </>
        ) : (
          comments.map((comment) => (
            <PlanningCommentItem
              key={comment.commentId}
              handleEdit={isReadOnly ? undefined : handleEdit}
              handleDelete={isReadOnly ? undefined : handleDelete}
              comment={comment}
              startOfPlan={startOfPlan}
              endOfPlan={endOfPlan}
              schedule={schedule}
            />
          ))
        )}
      </Stack>
    </>
  )
}
